import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import { groupIcon, groupDesc, groupLink, groupPrice, groupIsOrderable } from "@/lib/serviceGroups";

/**
 * The services a customer can order — driven by the template groups, not a list in this file.
 *
 * These four cards used to be hardcoded here and in the dashboard's quick-start row, so a group
 * with templates in it was unreachable: the document existed, was priced and rendered correctly,
 * and nothing linked to it.
 */
export default function NewDocument() {
  const [groups, setGroups] = useState([]);
  // null until loaded — an empty array would filter every card away on first paint
  const [templates, setTemplates] = useState(null);
  const [pricing, setPricing] = useState({});
  useEffect(() => {
    api.get("/template-groups").then(r => setGroups(r.data)).catch(() => {});
    api.get("/templates").then(r => setTemplates(r.data)).catch(() => {});
    api.get("/pricing").then(r => setPricing(r.data)).catch(() => {});
  }, []);

  return (
    <div className="space-y-8" data-testid="new-doc-page">
      <div>
        <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C]">New Document</div>
        <h1 className="font-serif-display text-4xl text-white mt-1">Choose a service</h1>
      </div>
      <div className="grid md:grid-cols-2 gap-5">
        {/* A group with nothing on sale is not offered at all */}
        {groups.filter(g => templates === null || groupIsOrderable(g.key, templates)).map(g => {
          const Icon = groupIcon(g);
          const total = groupPrice(g.key, pricing, templates);
          const count = (templates || []).filter(t => (t.group || "affidavit") === g.key && t.enabled !== false).length;
          return (
            <Link key={g.key} to={groupLink(g.key, templates)} data-testid={`new-opt-${g.key}`}>
              {/* Dark, like every other surface in the customer portal. These were the stock
                  shadcn Card — white (`--card: 0 0% 100%`) on a near-black page, which is the
                  palette used for a rendered stamp-paper sheet, not for a UI panel. They now
                  match the dashboard's tiles and table containers: one page, one design. */}
              <Card className="h-full bg-[#111623] border-white/10 hover:border-[#CBA96C]/40 rounded-2xl overflow-hidden transition-colors">
                <CardContent className="p-7">
                  <div className="flex items-start justify-between mb-5">
                    <div className="w-11 h-11 rounded-full bg-[#CBA96C]/10 border border-[#CBA96C]/20 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#CBA96C]" strokeWidth={1.5}/>
                    </div>
                    {total != null && (
                      <div className="text-right">
                        <div className="text-[10px] tracking-widest text-[#6B7280] uppercase">{count > 1 ? "From" : "Total"}</div>
                        <div className="font-serif-display text-2xl text-[#CBA96C]">₹{total.toFixed(0)}</div>
                      </div>
                    )}
                  </div>
                  <div className="font-serif-display text-2xl text-white">{g.label}</div>
                  <div className="text-sm text-[#A0AABF] mt-2">
                    {groupDesc(g) || (count ? `${count} document${count === 1 ? "" : "s"} available.` : "")}
                  </div>
                  <div className="text-sm mt-5 text-[#CBA96C] font-medium flex items-center gap-1">
                    Start <ArrowRight className="w-3.5 h-3.5"/>
                  </div>
                </CardContent>
              </Card>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
